import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { motion } from 'framer-motion';
import { Building, Check, X, Users, Mail, Power, AlertTriangle, Gauge } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboardPage = () => {
  const { t, getAuthHeaders } = useApp();
  const [stats, setStats] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [togglingMaintenance, setTogglingMaintenance] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchMaintenanceStatus();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/stats`, {
        headers: getAuthHeaders(),
      });
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchMaintenanceStatus = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/maintenance-status`);
      setMaintenanceMode(data.maintenance_mode);
    } catch (error) {
      console.error('Failed to fetch maintenance status');
    }
  };

  const toggleMaintenance = async () => {
    setTogglingMaintenance(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/maintenance/toggle`, {}, {
        headers: getAuthHeaders(),
      });
      setMaintenanceMode(data.maintenance_mode);
      toast.success(data.message);
    } catch (error) {
      toast.error('Failed to toggle maintenance mode');
    } finally {
      setTogglingMaintenance(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-4xl font-serif mb-8">{t('dashboard')}</h1>

          {/* Maintenance Mode Banner */}
          <div className={`mb-8 p-6 border-2 ${maintenanceMode ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                {maintenanceMode ? (
                  <AlertTriangle className="text-red-400" size={32} />
                ) : (
                  <Power className="text-green-400" size={32} />
                )}
                <div>
                  <h2 className="text-xl font-serif">
                    {maintenanceMode ? '🔴 Site-ul este OFFLINE' : '🟢 Site-ul este ONLINE'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {maintenanceMode 
                      ? 'Vizitatorii văd pagina de mentenanță. Admin-ul rămâne accesibil.'
                      : 'Site-ul este vizibil pentru toți vizitatorii.'}
                  </p>
                </div>
              </div>
              <Button
                onClick={toggleMaintenance}
                disabled={togglingMaintenance}
                className={maintenanceMode 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-red-600 hover:bg-red-700 text-white'}
              >
                <Power size={18} className="mr-2" />
                {togglingMaintenance 
                  ? 'Se procesează...' 
                  : maintenanceMode 
                    ? 'Activează Site-ul' 
                    : 'Pune în Mentenanță'}
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="glass p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-primary/10 border border-primary/20">
                  <Building className="text-primary" size={24} />
                </div>
                <div>
                  <div className="text-3xl font-serif text-primary">{stats?.total_apartments || 0}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t('total_apartments')}
                  </div>
                </div>
              </div>
            </div>

            <div className="glass p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-green-500/10 border border-green-500/20">
                  <Check className="text-green-400" size={24} />
                </div>
                <div>
                  <div className="text-3xl font-serif text-green-400">{stats?.available || 0}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('available')}</div>
                </div>
              </div>
            </div>

            <div className="glass p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-500/10 border border-red-500/20">
                  <X className="text-red-400" size={24} />
                </div>
                <div>
                  <div className="text-3xl font-serif text-red-400">{stats?.occupied || 0}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('occupied')}</div>
                </div>
              </div>
            </div>

            <div className="glass p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-blue-500/10 border border-blue-500/20">
                  <Users className="text-blue-400" size={24} />
                </div>
                <div>
                  <div className="text-3xl font-serif text-blue-400">{stats?.subscribers || 0}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('subscribers')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Second Row - Messages and Utility Indexes */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <a href="/admin/messages" className="glass p-6 hover:border-purple-500/50 transition-colors cursor-pointer relative">
              {stats?.unread_messages > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                  {stats.unread_messages}
                </div>
              )}
              <div className="flex items-center gap-4 mb-4">
                <div className={`p-3 border ${stats?.unread_messages > 0 ? 'bg-red-500/20 border-red-500/30' : 'bg-purple-500/10 border-purple-500/20'}`}>
                  <Mail className={stats?.unread_messages > 0 ? 'text-red-400' : 'text-purple-400'} size={24} />
                </div>
                <div>
                  <div className={`text-3xl font-serif ${stats?.unread_messages > 0 ? 'text-red-400' : 'text-purple-400'}`}>
                    {stats?.messages || 0}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t('messages')}
                    {stats?.unread_messages > 0 && (
                      <span className="ml-2 text-red-400">({stats.unread_messages} noi)</span>
                    )}
                  </div>
                </div>
              </div>
            </a>
            
            <a href="/admin/utility-index" className="glass p-6 hover:border-cyan-500/50 transition-colors cursor-pointer relative">
              {stats?.unread_indexes > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                  {stats.unread_indexes}
                </div>
              )}
              <div className="flex items-center gap-4 mb-4">
                <div className={`p-3 border ${stats?.unread_indexes > 0 ? 'bg-red-500/20 border-red-500/30' : 'bg-cyan-500/10 border-cyan-500/20'}`}>
                  <Gauge className={stats?.unread_indexes > 0 ? 'text-red-400' : 'text-cyan-400'} size={24} />
                </div>
                <div>
                  <div className={`text-3xl font-serif ${stats?.unread_indexes > 0 ? 'text-red-400' : 'text-cyan-400'}`}>
                    {stats?.utility_indexes || 0}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Indexuri
                    {stats?.unread_indexes > 0 && (
                      <span className="ml-2 text-red-400">({stats.unread_indexes} noi)</span>
                    )}
                  </div>
                </div>
              </div>
            </a>
          </div>

          {/* Quick Links */}
          <div className="glass p-8">
            <h2 className="text-2xl font-serif mb-4">Panou de Control RENTIASI.RO</h2>
            <p className="text-muted-foreground mb-4">
              Bine ai venit în panoul de administrare. De aici poți gestiona toate aspectele site-ului.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
              <a href="/admin/apartments" className="p-4 bg-slate-900/50 border border-white/10 hover:border-primary/30 transition-colors text-center">
                <Building className="mx-auto mb-2 text-primary" size={24} />
                <span className="text-sm">Apartamente</span>
              </a>
              <a href="/admin/newsletter" className="p-4 bg-slate-900/50 border border-white/10 hover:border-primary/30 transition-colors text-center">
                <Mail className="mx-auto mb-2 text-primary" size={24} />
                <span className="text-sm">Newsletter</span>
              </a>
              <a href="/admin/messages" className="p-4 bg-slate-900/50 border border-white/10 hover:border-primary/30 transition-colors text-center relative">
                {stats?.unread_messages > 0 && (
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {stats.unread_messages}
                  </div>
                )}
                <Mail className={`mx-auto mb-2 ${stats?.unread_messages > 0 ? 'text-red-400' : 'text-primary'}`} size={24} />
                <span className="text-sm">Mesaje</span>
              </a>
              <a href="/admin/utility-index" className="p-4 bg-slate-900/50 border border-white/10 hover:border-cyan-500/30 transition-colors text-center relative">
                {stats?.unread_indexes > 0 && (
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {stats.unread_indexes}
                  </div>
                )}
                <Gauge className={`mx-auto mb-2 ${stats?.unread_indexes > 0 ? 'text-red-400' : 'text-cyan-400'}`} size={24} />
                <span className="text-sm">Indexuri</span>
              </a>
              <a href="/admin/settings" className="p-4 bg-slate-900/50 border border-white/10 hover:border-primary/30 transition-colors text-center">
                <Power className="mx-auto mb-2 text-primary" size={24} />
                <span className="text-sm">Setări</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;